import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Import all locale files
import en from '../locales/en.json';
import ja from '../locales/ja.json';
import zh from '../locales/zh.json';
import vi from '../locales/vi.json';
import id from '../locales/id.json';
import ms from '../locales/ms.json';
import ru from '../locales/ru.json';

// Language configuration
export const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English', nativeName: 'English', countryCode: 'us' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', countryCode: 'jp' },
  { code: 'zh', name: 'Chinese', nativeName: '中文', countryCode: 'cn' },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', countryCode: 'vn' },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', countryCode: 'id' },
  { code: 'ms', name: 'Malay', nativeName: 'Bahasa Melayu', countryCode: 'my' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', countryCode: 'ru' }
];

// Default resources
const defaultResources = {
  en: { translation: en },
  ja: { translation: ja },
  zh: { translation: zh },
  vi: { translation: vi },
  id: { translation: id },
  ms: { translation: ms },
  ru: { translation: ru }
};

// Get custom translations from localStorage (for admin-uploaded translations)
const getCustomTranslations = () => {
  try {
    const custom = localStorage.getItem('custom_translations');
    return custom ? JSON.parse(custom) : {};
  } catch {
    return {};
  }
};

// Merge default resources with custom translations
const mergeResources = () => {
  const custom = getCustomTranslations();
  const resources = { ...defaultResources };
  
  Object.keys(custom).forEach(lang => {
    if (resources[lang]) {
      resources[lang] = {
        translation: { ...resources[lang].translation, ...custom[lang] }
      };
    } else {
      resources[lang] = { translation: custom[lang] };
    }
  });
  
  return resources;
};

i18n
  .use(initReactI18next)
  .init({
    resources: mergeResources(),
    lng: localStorage.getItem('language') || 'en',
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false
    },
    react: {
      useSuspense: false
    }
  });

// Function to reload translations (used after admin uploads new translations)
export const reloadTranslations = () => {
  const resources = mergeResources();
  Object.keys(resources).forEach(lang => {
    i18n.addResourceBundle(lang, 'translation', resources[lang].translation, true, true);
  });
};

// Function to save custom translations to localStorage
export const saveCustomTranslations = (lang, translations) => {
  const custom = getCustomTranslations();
  custom[lang] = translations;
  localStorage.setItem('custom_translations', JSON.stringify(custom));
  reloadTranslations();
};

// Function to export a language as JSON
export const exportLanguage = (langCode) => {
  const resources = i18n.getResourceBundle(langCode, 'translation');
  return JSON.stringify(resources, null, 2);
};

// Function to export all languages
export const exportAllLanguages = () => {
  const allLanguages = {};
  SUPPORTED_LANGUAGES.forEach(lang => {
    allLanguages[lang.code] = i18n.getResourceBundle(lang.code, 'translation');
  });
  return JSON.stringify(allLanguages, null, 2);
};

// Function to get language info
export const getLanguageInfo = (code) => {
  return SUPPORTED_LANGUAGES.find(lang => lang.code === code) || SUPPORTED_LANGUAGES[0];
};

// Function to check if a language is supported
export const isLanguageSupported = (code) => {
  return SUPPORTED_LANGUAGES.some(lang => lang.code === code);
};

export default i18n;
